import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { formatDMY, todayISO } from "@/lib/dateFormat";
import PageHeader from "@/components/PageHeader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { CalendarClock, CheckCircle2, AlertCircle, ChevronLeft, ChevronRight, Clock, Repeat, PauseCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDayLabel, currentCycleDates } from "@/lib/schedule";
import { attendanceChipClass, attendanceDotClass } from "@/lib/attendanceColors";
import CalendarSyncCard from "@/components/CalendarSyncCard";

export default function MyAttendance() {
  const { t, lang } = useI18n();
  const [att, setAtt] = useState([]);
  const [groups, setGroups] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [extraSessions, setExtraSessions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [groupId, setGroupId] = useState("all");
  // Cycle navigation: 0 = current cycle, -1 = previous, +1 = next (per enrollment)
  const [cycleOffset, setCycleOffset] = useState(0);

  useEffect(() => {
    (async () => {
      const [a, g, e, p, x, pay] = await Promise.all([
        api.get("/attendance"),
        api.get("/groups"),
        api.get("/enrollments?with_consumption=true"),
        api.get("/programs"),
        api.get("/extra-sessions"),
        api.get("/payments").catch(() => ({ data: [] })),
      ]);
      setAtt(a.data); setGroups(g.data); setEnrollments(e.data); setPrograms(p.data); setExtraSessions(x.data || []);
      setPayments(pay.data || []);
    })();
  }, []);

  const groupsMap = useMemo(() => Object.fromEntries(groups.map((x) => [x.id, x.name])), [groups]);
  const programsMap = useMemo(() => Object.fromEntries(programs.map((x) => [x.id, x])), [programs]);
  const filtered = groupId === "all" ? att : att.filter((a) => a.group_id === groupId);
  const [showAllHistory, setShowAllHistory] = useState(false);

  const counts = useMemo(() => {
    const c = { present: 0, justified: 0, unjustified: 0, teacher_cancelled: 0, absent: 0, unjustified_consumed: 0 };
    filtered.forEach((a) => { if (c[a.status] !== undefined) c[a.status]++; });
    // Roll legacy "absent" records and "unjustified_consumed" (from rejected
    // absence requests) into "unjustified" so the visible KPI cards
    // (4 buckets) still reflect them. Old records remain in the underlying table.
    c.unjustified += c.absent + c.unjustified_consumed;
    return c;
  }, [filtered]);

  // Compute per-enrollment progress using the SAME calendar logic used by
  // "Mi ciclo actual". This keeps both cards perfectly in sync so students see
  // the same numbers regardless of whether the payment has been marked paid yet.
  const myCycles = useMemo(() => {
    const out = [];
    for (const enr of enrollments) {
      const g = groups.find((x) => x.id === enr.group_id);
      if (!g) continue;
      const program = programsMap[enr.program_id];
      const totalPerCycle = enr.classes_included || program?.classes_included || 0;
      const cc = currentCycleDates({
        group: g, enrollment: enr, extraSessions, attendance: att, totalPerCycle,
      });
      if (!cc) continue;
      out.push({
        ...enr,
        consumption: {
          consumed: cc.consumed, total: cc.total, remaining: cc.remaining,
          pct: cc.pct, cycle_index: cc.cycle,
        },
      });
    }
    return out;
  }, [enrollments, groups, programsMap, att, extraSessions]);

  // Tarjetas de "Mi progreso": siguen el ciclo que se está viendo con las
  // flechas, no siempre el actual (antes te movías al ciclo 2 y arriba seguían
  // los números del ciclo 1 — reportado el 10/09/2026).
  const displayCycles = useMemo(() => {
    const out = [];
    for (const enr of enrollments) {
      const g = groups.find((x) => x.id === enr.group_id);
      if (!g) continue;
      const program = programsMap[enr.program_id];
      const totalPerCycle = enr.classes_included || program?.classes_included || 0;
      const cc = currentCycleDates({
        group: g, enrollment: enr, extraSessions, attendance: att, totalPerCycle,
      });
      if (!cc) continue;
      const targetCycle = cc.cycle + cycleOffset;
      const dates = cc.classified.filter((d) => d.cycle === targetCycle);
      const consumed = dates.filter((d) => d.consumes && d.status).length
        + (targetCycle === 1 ? Number(enr.starts_on_day || 0) : 0);
      const total = cc.total;
      out.push({
        ...enr,
        consumption: {
          consumed, total,
          remaining: Math.max(0, total - consumed),
          pct: total > 0 ? Math.round((consumed / total) * 100) : 0,
          cycle_index: targetCycle,
        },
      });
    }
    return out;
  }, [enrollments, groups, programsMap, att, extraSessions, cycleOffset]);

  // Per-group cycle rows (shared by the chips section and the per-cycle stat cards).
  const cycleView = useMemo(() => {
    const enrollByGroup = new Map();
    enrollments.forEach((e) => { enrollByGroup.set(e.group_id, e); });
    const groupRows = enrollments
      .map((e) => groups.find((g) => g.id === e.group_id))
      .filter(Boolean)
      .filter((g, i, arr) => arr.findIndex((x) => x.id === g.id) === i);
    const rows = [];
    for (const g of groupRows) {
      const enr = enrollByGroup.get(g.id);
      if (!enr) continue;
      const program = programsMap[enr.program_id];
      const totalPerCycle = enr.classes_included || program?.classes_included || 0;
      const cc = currentCycleDates({
        group: g, enrollment: enr, extraSessions, attendance: att, totalPerCycle,
      });
      if (!cc) continue;
      const targetCycle = cc.cycle + cycleOffset;
      const cycleDates = cc.classified.filter((d) => d.cycle === targetCycle);
      const consumedInCycle = cycleDates.filter((d) => d.consumes && d.status).length
        + (targetCycle === 1 ? Number(enr.starts_on_day || 0) : 0);
      rows.push({ g, enr, totalPerCycle, targetCycle, cycleDates, swapMap: cc.swapMap, consumedInCycle });
    }
    // Stat cards follow the displayed cycle (all groups, or the selected one)
    const cycleCounts = { present: 0, justified: 0, unjustified: 0, teacher_cancelled: 0 };
    rows.filter((r) => groupId === "all" || r.g.id === groupId).forEach((r) => {
      r.cycleDates.forEach((d) => {
        const st = d.status === "absent" || d.status === "unjustified_consumed" ? "unjustified" : d.status;
        if (st && cycleCounts[st] !== undefined) cycleCounts[st]++;
      });
    });
    const cycleKeys = new Set();
    rows.filter((r) => groupId === "all" || r.g.id === groupId).forEach((r) => {
      r.cycleDates.forEach((d) => cycleKeys.add(`${r.g.id}|${d.date}`));
    });
    const allTimePresent = att.filter((a) => a.status === "present").length;
    const nums = [...new Set(rows.filter((r) => groupId === "all" || r.g.id === groupId).map((r) => r.targetCycle))].sort((a, b) => a - b);
    const cycleLabel = nums.length ? `${t("cycle")} ${nums.join(", ")}${cycleOffset === 0 ? ` · ${t("cycle_current").toLowerCase()}` : ""}` : t("cycle_current");
    return { rows, cycleCounts, allTimePresent, cycleLabel, cycleKeys };
  }, [enrollments, groups, extraSessions, programsMap, att, cycleOffset, groupId, t]);

  // Tabla día a día: por defecto solo las fechas del ciclo mostrado.
  const tableRows = useMemo(() => {
    if (showAllHistory || !cycleView.cycleKeys.size) return filtered;
    return filtered.filter((a) => cycleView.cycleKeys.has(`${a.group_id}|${a.date}`));
  }, [filtered, cycleView, showAllHistory]);

  return (
    <div>
      <PageHeader title={t("my_attendance")} testId="my-attendance-header" />

      <div className="mb-6"><CalendarSyncCard compact /></div>

      {myCycles.length > 0 && (
        <div className="mb-6" data-testid="my-progress-section">
          <h2 className="font-display text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-brand-blue" />
            {t("my_progress")}
          </h2>
          {/* Renewal banner: reminder from 75%. The pay button only shows up
              when there IS an unpaid invoice — at 75% with everything up to
              date the student only gets the heads-up. */}
          {(() => {
            const needsRenew = myCycles.find((e) => e.consumption.pct >= 75);
            if (!needsRenew) return null;
            const c = needsRenew.consumption;
            const done = c.consumed >= c.total;
            const openInvoice = payments.find(
              (p) => p.enrollment_id === needsRenew.id && p.status !== "paid",
            );
            const payHref = openInvoice ? needsRenew.payment_link : null;
            const msg = openInvoice
              ? (done ? t("renew_banner_done_pending") : t("renew_banner_75_pending").replace("{n}", c.remaining))
              : (done ? t("renew_banner_done") : t("renew_banner_75").replace("{n}", c.remaining));
            return (
              <div
                data-testid="renew-banner"
                className={`mb-3 flex items-center justify-between gap-3 p-3 rounded-lg border ${
                  openInvoice ? "bg-amber-50 border-amber-300"
                    : done ? "bg-emerald-50 border-emerald-200"
                    : "bg-orange-50 border-orange-200"
                }`}
              >
                <p className={`text-sm font-medium ${
                  openInvoice ? "text-amber-900" : done ? "text-emerald-800" : "text-orange-800"
                }`}>
                  {msg}
                </p>
                {payHref && (
                  <a
                    href={payHref}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="renew-banner-pay-button"
                    className="bg-brand text-white px-4 py-2 rounded-md text-sm font-semibold hover:opacity-90 whitespace-nowrap"
                  >
                    {t("pay_now")}
                  </a>
                )}
                {openInvoice && !payHref && (
                  <Link
                    to="/dashboard/my-payments"
                    data-testid="renew-banner-invoice-link"
                    className="bg-white text-slate-700 border border-slate-300 px-4 py-2 rounded-md text-sm font-semibold hover:bg-slate-50 whitespace-nowrap"
                  >
                    {t("view_payment_details")}
                  </Link>
                )}
              </div>
            );
          })()}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {displayCycles.map((e) => {
              const c = e.consumption;
              const isDone = c.consumed >= c.total;
              const barColor = isDone ? "bg-emerald-500"
                : c.pct >= 75 ? "bg-orange-500"
                : c.pct >= 50 ? "bg-brand-blue"
                : "bg-slate-400";
              const program = programsMap[e.program_id];
              return (
                <Card key={e.id} className="p-4" data-testid={`my-cycle-${e.id}`}>
                  <div className="flex items-start gap-3">
                    {isDone ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle className={`h-5 w-5 ${c.pct >= 75 ? "text-orange-500" : "text-slate-400"} flex-shrink-0 mt-0.5`} />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-medium text-slate-900 truncate">{groupsMap[e.group_id] || e.group_id}</span>
                        <span className={`text-xs font-semibold whitespace-nowrap ${
                          isDone ? "text-emerald-700"
                            : c.pct >= 75 ? "text-orange-700"
                            : "text-slate-600"
                        }`} data-testid={`my-cycle-progress-${e.id}`}>
                          {t("cycle")} {c.cycle_index} · {c.consumed}/{c.total} · {c.pct}%
                        </span>
                      </div>
                      {program?.name && (
                        <p className="text-xs text-slate-500 mt-0.5">{program.name}</p>
                      )}
                      <div className="h-1.5 mt-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(c.pct, 100)}%` }} />
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500">
                        <span>{t("remaining")}: <strong className="text-slate-700">{c.remaining}</strong></span>
                        {e.start_date && (
                          <span>{t("start_date")}: <strong className="text-slate-700">{formatDMY(e.start_date, lang)}</strong></span>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Per-group cycle visualization: shows the in-progress block of classes for each enrollment */}
      {(() => {
        if (cycleView.rows.length === 0) return null;
        const today = todayISO();

        return (
          <div className="mb-6" data-testid="my-attendance-cycle-section">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <h2 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2">
                <CalendarClock className="h-4 w-4 text-brand-blue" />
                {cycleOffset === 0 ? t("my_current_cycle") : cycleOffset < 0 ? t("my_previous_cycle") : t("my_next_cycle")}
              </h2>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" onClick={() => setCycleOffset((v) => v - 1)} data-testid="my-att-prev-cycle">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-3 text-sm font-medium text-slate-700 min-w-[110px] text-center" data-testid="my-att-cycle-label">
                  {cycleView.cycleLabel}
                </span>
                <Button variant="outline" size="icon" onClick={() => setCycleOffset((v) => v + 1)} data-testid="my-att-next-cycle">
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setCycleOffset(0)} data-testid="my-att-this-cycle">{t("cycle_current")}</Button>
              </div>
            </div>
            <div className="space-y-3">
              {cycleView.rows.map(({ g, totalPerCycle, targetCycle, cycleDates, swapMap, consumedInCycle }) => {
                if (cycleDates.length === 0) {
                  return (
                    <Card key={g.id} className="p-4" data-testid={`my-att-group-${g.id}`}>
                      <div className="text-sm font-medium text-slate-900 mb-2">{g.name}</div>
                      <p className="text-xs text-slate-400 italic">{t("no_dates_in_cycle")}</p>
                    </Card>
                  );
                }
                return (
                  <Card key={g.id} className="p-4" data-testid={`my-att-group-${g.id}`}>
                    <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                      <div className="text-sm font-medium text-slate-900">{g.name}</div>
                      <div className="text-[11px] font-semibold text-slate-600">
                        {t("cycle")} {targetCycle} · {consumedInCycle}/{totalPerCycle}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {cycleDates.map(({ date: d, status, paused }) => {
                        const isFuture = d > today;
                        const isToday = d === today;
                        const rescheduled = swapMap[d];
                        // Unified palette. Today (no record) gets a subtle blue nudge.
                        let cls = attendanceChipClass(paused ? "paused" : status);
                        if (!status && !paused && isToday) cls = "bg-blue-100 text-blue-900 border-blue-500";
                        return (
                          <div
                            key={d}
                            className={`px-2.5 py-1 rounded-md text-[11px] font-semibold border flex items-center gap-1.5 ${cls}`}
                            data-testid={`my-att-chip-${g.id}-${d}`}
                            title={paused ? `${d} — ${t("att_day_paused")}` : rescheduled ? t("rescheduled_from", { date: rescheduled.original }) : (status ? `${d} — ${t(status)}` : d)}
                          >
                            {paused ? <PauseCircle className="h-3 w-3" />
                              : rescheduled ? <Repeat className="h-3 w-3" />
                              : status === "present" ? <CheckCircle2 className="h-3 w-3" />
                              : !status && !isFuture ? <Clock className="h-3 w-3" />
                              : null}
                            {formatDayLabel(d, lang)}
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-3 text-[10px] text-slate-500">
              {["present","justified","unjustified","teacher_cancelled","not_counted","removed",""].map((s) => (
                <span key={s || "pending"} className="flex items-center gap-1">
                  <span className={`inline-block h-2.5 w-2.5 rounded-full ${attendanceDotClass(s)}`} />
                  {s ? t(s) : t("upcoming")}
                </span>
              ))}
            </div>
          </div>
        );
      })()}
      {cycleView.rows.length > 0 && (
        <p className="text-xs uppercase tracking-wider text-slate-500 mb-2" data-testid="cycle-stats-title">
          {t("cycle_stats_title")} · {cycleView.cycleLabel}
        </p>
      )}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <div className="bg-brand-blue/5 border border-brand-blue/20 rounded-lg p-4" data-testid="all-time-present">
          <div className="text-xs uppercase tracking-wider text-brand-blue">{t("all_time_present")}</div>
          <div className="font-display text-3xl font-bold mt-1 text-brand-blue">{cycleView.allTimePresent}</div>
        </div>
        {["present","justified","unjustified","teacher_cancelled"].map((s) => (
          <div key={s} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`cycle-stat-${s}`}>
            <div className="text-xs uppercase tracking-wider text-slate-500">{t(s)}</div>
            <div className="font-display text-3xl font-bold mt-1">
              {cycleView.rows.length > 0 ? cycleView.cycleCounts[s] || 0 : counts[s] || 0}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
      <div className="max-w-md">
        <Label>{t("group")}</Label>
        <Select value={groupId} onValueChange={setGroupId}>
          <SelectTrigger data-testid="my-attendance-group-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("total")}</SelectItem>
            {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
        {cycleView.rows.length > 0 && (
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={showAllHistory} onChange={(e) => setShowAllHistory(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300" data-testid="my-att-history-toggle" />
            {t("show_all_history")}
          </label>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>{t("date")}</TableHead>
              <TableHead>{t("group")}</TableHead>
              <TableHead>{t("status")}</TableHead>
              <TableHead>{t("notes")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tableRows.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-10">{t("no_data")}</TableCell></TableRow>
            )}
            {tableRows.map((a) => (
              <TableRow key={a.id} data-testid={`my-att-row-${a.id}`}>
                <TableCell>{formatDMY(a.date, lang)}</TableCell>
                <TableCell>{groupsMap[a.group_id] || a.group_id}</TableCell>
                <TableCell><StatusBadge status={a.status} /></TableCell>
                <TableCell className="text-slate-600">{a.notes || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
